import type { LocationDescriptorObject } from 'history';

import type { CtaLine } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import type { ConnectedPiece } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulLine';
import {
  BigButton,
  BigButtonFullWidthMdAndDown,
  BigButtonFullWidthSmAndDown,
  BigButtonNoExpand,
  BigButtonSecondary,
  BigButtonSecondaryLessPadding,
  Link,
  LinkOnDark,
  SmallButton,
} from 'bundles/coursera-plus-landing-page/components/ctaContentful/EnrollButtonPresentationContentful';
import type { CourseraPlusCtaPartPicked } from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';
import CourseraPlusProductVariants, {
  courseraLiteProductVariants,
} from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import { isMonthlyByVariantId as isMonthly } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';

import _t from 'i18n!nls/coursera-plus-landing-page';

export const HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE = 'insite';

export const landingPageTypeChoices = {
  COMPUTER_SCIENCE_COLLECTION: 'Bundles Landing Page (Computer Science)',
  PURCHASE_TRACKER: 'Purchase Tracker Landing Page',
  COURSERA_LITE_LANDING_PAGE: 'Coursera Lite Landing Page',
};

export const contentfulValuesForProductVariant = {
  'Monthly with Free Trial': CourseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
  'Annual with No Free Trial': CourseraPlusProductVariants.ANNUAL_NO_FREE_TRIAL,
  'Annual special pricing with no free trial':
    CourseraPlusProductVariants.ANNUAL_SPECIAL_DISCOUNTED_PRICING_NO_FREE_TRIAL,
  'Coursera Lite Monthly with Free Trial': courseraLiteProductVariants.MONTHLY_WITH_FREE_TRIAL,
} as const;

export const getProductFromContentful = (contentful: string): SubscriptionTiersProductVariant | undefined => {
  const value = Object.entries(contentfulValuesForProductVariant).find((e) => {
    return e[0] === contentful;
  });
  return value ? value[1] : undefined;
};

export const PRODUCT_CHOICES = {
  primary: 'Primary',
  secondary: 'Secondary',
};

export const CTA_LOCATIONS = {
  hero: 'hero',
  footer: 'footer',
  sticky: 'sticky_banner',
  learnerStats: 'learner_statistics',
  collections: 'collections',
};

export const PRESENTATIONS = {
  plain: 'Plain',
  bigButton: 'Big button',
  smallButton: 'Small Button',
  link: 'Link',
};

interface SomeCtaPiece {
  presentation?: string | null;
}

export const isPieceForCheckout = (piece: SomeCtaPiece): boolean => {
  return piece.presentation !== PRESENTATIONS.plain;
};

export const isPresentationButton = (presentation: string): boolean => {
  if (presentation === PRESENTATIONS.bigButton || presentation === PRESENTATIONS.smallButton) {
    return true;
  }
  return false;
};

export const pieceForPrimaryButton = (
  presentation: $TSFixMe,
  label: $TSFixMe,
  isFirstCheckoutInHero: boolean
): ConnectedPiece => ({
  product: PRODUCT_CHOICES.primary,
  presentation,
  messages: [{ entryTitle: null, pretranslated: null, freeform: label }],
  isFirstCheckoutInHero,
});

export const artificialCtaLines = (label: string): Array<CtaLine> => [
  {
    lineNumber: 1,
    firstLineWithCheckout: true,
    pieces: [{ product: PRODUCT_CHOICES.primary, freeform: label, presentation: PRESENTATIONS.bigButton }],
  },
];

export const searchLabel = (): string => _t('Search Coursera Plus');

export const isActionPresentation = (presentation: string | null | undefined) => {
  return typeof presentation === 'string' && presentation !== PRESENTATIONS.link;
};

const LOCATIONS_WITH_SECONDARY_BUTTON = [CTA_LOCATIONS.learnerStats, CTA_LOCATIONS.footer];

export const actionComponent = (presentation: string, singleBigButton: boolean, ctaLocation: string) => {
  if (presentation === PRESENTATIONS.bigButton) {
    if (LOCATIONS_WITH_SECONDARY_BUTTON.includes(ctaLocation)) {
      return BigButtonSecondary;
    }
    if (ctaLocation === CTA_LOCATIONS.sticky) {
      return BigButtonFullWidthMdAndDown;
    }
    if (ctaLocation === CTA_LOCATIONS.hero) {
      return BigButtonFullWidthSmAndDown;
    }
    if (ctaLocation === CTA_LOCATIONS.collections) {
      return BigButtonSecondaryLessPadding;
    }
    if (singleBigButton) {
      return BigButton;
    }
    return BigButtonNoExpand;
  }
  if (presentation === PRESENTATIONS.smallButton) {
    return SmallButton;
  }
  if (ctaLocation === CTA_LOCATIONS.footer) {
    return LinkOnDark;
  }
  return Link;
};

const preview = 'preview';
const noFreeTrial = 'nofreetrial';

const isMarkedInSearch = (location: LocationDescriptorObject, key: string) => {
  const keys = Object.keys(location.query ?? {});
  return !!keys.find((k) => k.toLocaleLowerCase() === key);
};

export const isPreview = (router: $TSFixMe) => {
  return isMarkedInSearch(router.location, preview);
};

export const isMockNoTrial = (location: LocationDescriptorObject) => {
  return isMarkedInSearch(location, noFreeTrial);
};

export const SHOW_PART_CONDITIONS = {
  freeTrialAvailable: 'Only if free trial is available',
  freeTrialNotAvailable: 'Only if free trial is not available',
};

export const showPart = (part: CourseraPlusCtaPartPicked, hasFreeTrial: boolean): boolean => {
  const { conditionOfShowing } = part;
  if (conditionOfShowing === SHOW_PART_CONDITIONS.freeTrialAvailable) {
    return hasFreeTrial;
  }
  if (conditionOfShowing === SHOW_PART_CONDITIONS.freeTrialNotAvailable) {
    return !hasFreeTrial;
  }
  return true;
};

export const renewSubscriptionLabel = (): string => _t('Renew Subscription');

export const indicateRedirectedFromExpired = 'redirectedfromexpired';

export const HERO_AND_FOOTER_CTA_TRACKING_NAMES = {
  HERO_MONTHLY: 'hero_monthly',
  HERO_ANNUAL: 'hero_annual',
  FOOTER_MONTHLY: 'footer_monthly',
  FOOTER_ANNUAL: 'footer_annual',
} as const;

export const getCourseraPlusCTATrackingName = ({
  productItemId,
  ctaLocation,
}: {
  productItemId: SubscriptionTiersProductVariant;
  ctaLocation: string;
}) => {
  if (ctaLocation === CTA_LOCATIONS.hero) {
    return isMonthly(productItemId)
      ? HERO_AND_FOOTER_CTA_TRACKING_NAMES.HERO_MONTHLY
      : HERO_AND_FOOTER_CTA_TRACKING_NAMES.HERO_ANNUAL;
  } else if (ctaLocation === CTA_LOCATIONS.footer) {
    return isMonthly(productItemId)
      ? HERO_AND_FOOTER_CTA_TRACKING_NAMES.FOOTER_MONTHLY
      : HERO_AND_FOOTER_CTA_TRACKING_NAMES.FOOTER_ANNUAL;
  } else {
    return ctaLocation + '_' + (isMonthly(productItemId) ? 'monthly' : 'annual');
  }
};
